import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readSlideMasters } from "@/office/powerpoint/design";
import { nichtPruefbar } from "../schnittstelle";

export const lektionDesign: Lesson = {
  id: "design",
  title: "Designs verstehen",
  summary: "Ein Design ist ein Farb-, Schrift- und Formenpaket – kein Hintergrundbild.",
  durationMinutes: 9,
  learningGoals: [
    "Ein Design auswählen und seine Varianten nutzen",
    "Erkennen, was ein Design alles festlegt",
    "Mit genau einem Folienmaster arbeiten",
  ],
  steps: [
    {
      id: "was-ist-ein-design",
      title: "Was ein Design festlegt",
      explanation: [
        "Auf der Registerkarte „Entwurf“ steht eine Reihe von Designs zur Auswahl. Was " +
          "dort wie eine Sammlung hübscher Hintergründe aussieht, ist in Wahrheit ein " +
          "vollständiges Gestaltungspaket: zwölf Farben, zwei Schriftarten – eine für " +
          "Überschriften, eine für Fließtext –, ein Satz Effekte für Formen und die " +
          "Hintergrundgestaltung.",
        "Der Punkt, auf den es ankommt: Diese Angaben werden nicht in die Folien " +
          "kopiert, sondern verweisend genutzt. Ein Titel ist nicht „dunkelblau“, sondern " +
          "„Designfarbe Dunkel 2“. Wechselst du das Design, ändert sich der Wert hinter " +
          "dem Verweis – und alle Titel wechseln mit.",
        "Genau deshalb funktioniert ein Designwechsel überhaupt. Und genau deshalb bricht " +
          "er bei Folien, deren Farben und Schriften von Hand gesetzt wurden: Dort steht " +
          "kein Verweis, sondern ein fester Wert.",
        "Rechts neben den Designs stehen die Varianten. Sie behalten Layout und Schriften " +
          "des Designs bei und tauschen nur die Farbstimmung – oft die schnellste Art, " +
          "eine Präsentation an eine Hausfarbe anzunähern, ohne alles neu zu bauen.",
      ],
      keyPoints: [
        "Entwurf → Designs und daneben die Varianten.",
        "Ein Design legt Farben, Schriften, Effekte und Hintergrund fest.",
        "Von Hand gesetzte Farben überstehen einen Designwechsel nicht.",
      ],
    },
    {
      id: "ein-folienmaster",
      title: "Ein Folienmaster, nicht drei",
      explanation: [
        "Eine Präsentation kann mehrere Folienmaster enthalten. Gewollt ist das fast nie – " +
          "es passiert, wenn Folien aus einer anderen Datei mit der Option „Ursprüngliche " +
          "Formatierung beibehalten“ eingefügt werden.",
        "Die Folgen merkt man erst später: Ein Designwechsel wirkt nur auf die Folien des " +
          "einen Masters, die anderen behalten ihr altes Aussehen. Änderungen am " +
          "Folienmaster erreichen nur einen Teil der Präsentation. Und in der " +
          "Layoutauswahl stehen plötzlich zwei Blöcke mit fast gleich heißenden Layouts.",
        "Sichtbar wird das unter „Ansicht“ → „Folienmaster“: Links stehen dann mehrere " +
          "große Miniaturen mit je einem eigenen Satz Layouts darunter. Ein überflüssiger " +
          "Master lässt sich dort per Rechtsklick löschen – die Folien, die ihn " +
          "verwenden, wandern automatisch auf den verbleibenden Master.",
        "Vorbeugen ist einfacher: Beim Einfügen fremder Folien immer " +
          "„Zielformatierung verwenden“ wählen.",
      ],
      keyPoints: [
        "Ansicht → Folienmaster zeigt, wie viele Master es gibt.",
        "Überflüssige Master per Rechtsklick löschen.",
        "Beim Einfügen fremder Folien „Zielformatierung verwenden“ wählen.",
      ],
      task: {
        instruction:
          "Prüfe, dass deine Präsentation genau einen Folienmaster verwendet.",
        hint: "Ansicht → Folienmaster. Steht links mehr als eine große Miniatur, gibt es mehrere.",
        check: async () => {
          const master = await readSlideMasters();

          if (master === null) {
            return nichtPruefbar(
              "Die Folienmaster der Präsentation",
              "1.3",
              "Öffne „Ansicht“ → „Folienmaster“ und zähle die großen Miniaturen links.",
            );
          }

          if (master.length === 1) {
            return pass(
              `Genau ein Folienmaster („${master[0].name || "unbenannt"}“) mit ${master[0].layouts.length} Layouts – ` +
                "ein Designwechsel erfasst damit die ganze Präsentation.",
            );
          }
          if (master.length === 0) {
            return fail(
              "Es wurde kein Folienmaster gefunden.",
              "Lege zuerst eine Folie an – jede Präsentation bringt dann automatisch einen Master mit.",
            );
          }

          return fail(
            `Die Präsentation hat ${master.length} Folienmaster: ${master
              .map((eintrag) => `„${eintrag.name || "unbenannt"}“`)
              .join(", ")}.`,
            "Unter „Ansicht“ → „Folienmaster“ die überflüssigen Master per Rechtsklick löschen. " +
              "Die betroffenen Folien wechseln dann automatisch auf den verbleibenden Master.",
          );
        },
      },
    },
    {
      id: "eigene-designs",
      title: "Ein Design als eigenes speichern",
      explanation: [
        "Wenn Farben, Schriften und Layouts einmal so eingestellt sind, wie sie sein " +
          "sollen, lässt sich das Ergebnis als eigenes Design speichern: „Entwurf“ → das " +
          "Aufklappmenü am rechten Rand der Designleiste → „Aktuelles Design speichern“. " +
          "Es erscheint danach in der Designleiste ganz oben unter „Benutzerdefiniert“.",
        "Das ist etwas anderes als eine Vorlage. Ein Design (.thmx) enthält nur die " +
          "Gestaltung und lässt sich auf jede beliebige Präsentation anwenden – auch auf " +
          "eine bestehende. Eine Vorlage (.potx) enthält zusätzlich Folien, Platzhaltertexte " +
          "und Beispielinhalte und ist der Startpunkt für eine neue Präsentation. Beides " +
          "hat seinen Platz; dazu mehr im Modul „Vorlagen und Corporate Design“.",
        "Für ein Team ist das Design oft der praktischere Weg: Es lässt sich auf bereits " +
          "begonnene Präsentationen anwenden, ohne dass jemand von vorn anfangen muss.",
      ],
      keyPoints: [
        "Entwurf → Aufklappmenü → Aktuelles Design speichern (.thmx).",
        "Design = Gestaltung, Vorlage = Gestaltung plus Startinhalte.",
        "Ein Design lässt sich auf bestehende Präsentationen anwenden.",
      ],
    },
  ],
};
